/* eslint-disable prettier/prettier */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import _, { trimEnd } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import {
    hooks as commonHooks,
    types as commonTypes,
    baseComponents,
    utils as commonUtils,
} from '@sitebuilder/common';
import { Headings } from '../Header/Header';
import HeaderWrapper from '../Header/HeaderWrapper';
import IconIndicator from '../IconIndicator/IconIndicator';
import RiskIndicator from '../RiskIndicator/RiskIndicator';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import tableStyles, { themetableStyles } from './Table.styles';

const CHILDREN_PLACEHOLDER_ID = 'children';



export interface TableColumn {
    label?: string;
    value?: string;
    downloadLabel?: string;
    downloadValue?: string;
    isOnlyText?: boolean;
    isSerial?: boolean;
    customDataKey?: string;
    isResourceString?: boolean;
    isIcon?: boolean;
    markerLabel?: string;
    columnIsUnique?: boolean;
    isLink?: boolean;
    linkIcon?: string;
    linkUrl?: string;
    disableVisibilityToggle?: boolean;
    id?: string;
}

interface IconIndicators {
    activeIconName?: string;
    inactiveIconName?: string;
}

export interface TableOptions {
    id?: string;
    columns?: TableColumn[];
    rows?: string;
    chartTitles: Headings[];
    isCollapsible?: boolean;
    defaultCollapse?: string;
    collapsibleElement?: string;
    collapsibleElementPosition?: string;
    collapsibleShowLabel?: string;
    collapsibleHideLabel?: string;
    excludedRow?: number;
    iconShowContent?: string;
    iconHideContent?: string;
    iconIndicators?: IconIndicators[];
    transformRowsData?: boolean;
    functionToTransformRows?: string;
    noDataMessage: string;
    newFundDataMessage: string;
    emptyRowsMessage: string;
    nullValueAriaLabel: string;
    filterData: boolean;
    filterBy: string;
    filterWithUrlParam: boolean;
    filterUrlParam: string;
    filterWith: string;
    disableTabStaticData?: boolean;
    typographyClass?: string;
    enableColumnToggling?: boolean;
    columnTogglingTitle?: string;
    columnTogglingSubtitle?: string;
    columnTogglingSaveText?: string;
    columnTogglingCancelText?: string;
    columnTogglingSelectAllText?: string;
    columnTogglingDeselectAllText?: string;
    enableRowToggling?: boolean;
    rowTogglingTitle?: string;
    rowTogglingSubtitle?: string;
    rowTogglingSaveText?: string;
    rowTogglingCancelText?: string;
    rowTogglingSelectAllText?: string;
    rowTogglingDeselectAllText?: string;
    rowTogglingLabelDataKey?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, TableOptions {}

interface Widgetprops extends commonTypes.WidgetInstanceProps {
    pricesData?: any;
}

const Table = (props: Widgetprops) => {
    
    const { storyData, pricesData } = props;
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const { styles, columns, rows, ...rest } = widgetProperties || props;

    const i18n = commonHooks.useLocalization();
    const annotator = commonHooks.useAnnotator();
    const storeData = props.data;
    const theme = storeData?.themeSelected || props?.theme;
    const _data: any = storyData || storeData;
    let rowsData;
    try {
        rowsData = !pricesData ? JsonQuery(rows as string, { data: _data }).value : pricesData;
        // Ensure rowsData is always an array to prevent null/undefined issues
        if (!Array.isArray(rowsData)) {
            rowsData = [];
        }
    } catch (error) {
        rowsData = [];
    }
    
    const location = useLocation();
    const search = location.search;
    const tabIndex = globalDisableTabStaticData || rest.disableTabStaticData ? -1 : 0;
    const { Placeholder, inEditMode } = commonHooks.useAppContext();

    let isRender: any = {
        title: true,
        table: true,
        transformedRowsData: rowsData,
        transformedData: _data,
        columns: columns?.map((c, i) => ({ ...c, id: `${i}` })),
        newFundDataMessage: rest.newFundDataMessage,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedData =
            rest.transformRowsData &&
            commonUtils.transformDataWithGlobals(
                transformFunctionName,
                rest.functionToTransformRows,
                {
                    rowsData,
                    data: _data,
                    annotator,
                    ...isRender,
                    JsonQuery,
                    i18n,
                }
            );

        if (transformedData) {
            isRender = { ...isRender, ...transformedData };
            
            // In edit mode, ensure table is visible even if transform function hides it
            // This allows editors to see and configure tables that might be conditionally hidden
            if (inEditMode && transformedData.table === false) {
                isRender.table = true;
                // Add a flag to indicate this is an override for styling purposes
                isRender.tableVisibilityOverride = true;
            }
        }
    } catch (e) {
        console.log(e);
    }

    const data = isRender?.transformedData;

    if (rest.filterData) {
        let list: any;
        if (rest.filterWithUrlParam) {
            const params = new URLSearchParams(search);
            const filterList = params.get(rest.filterUrlParam);
            list = filterList?.split('|');
            if (list && list.filter((l: string) => l).length > 0) {
                isRender = {
                    ...isRender,
                    transformedRowsData: isRender?.transformedRowsData?.filter((r: any) =>
                        list?.includes(r[rest.filterBy])
                    ),
                };
            }
        } else {
            list = JsonQuery(rest.filterBy as string, { data }).value;
            if (list && list.filter((l: string) => l).length > 0) {
                isRender = {
                    ...isRender,
                    transformedRowsData: isRender?.transformedRowsData?.filter((r: any) =>
                        list?.includes(r[rest.filterBy])
                    ),
                };
            }
        }
    }

    const prevColumnsRef = useRef<TableColumn[]>(isRender.columns);
    const [showSection, setShowSection] = useState<boolean>(rest.defaultCollapse === 'Show');
    const [shownColumns, setShownColumns] = useState<TableColumn[]>(isRender.columns || []);
    const [shownRows, setShownRows] = useState<any[]>([]);

    useEffect(() => {
        // Add robust null checking to prevent "Cannot read properties of null (reading 'length')" errors
        if (isRender?.transformedRowsData && Array.isArray(isRender.transformedRowsData) && 
            isRender.transformedRowsData.length > 0 && shownRows.length === 0) {
            setShownRows(isRender.transformedRowsData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRender?.transformedRowsData]); // This ensures rows are shown by default until a user manually changes them

    useEffect(() => {
        // Ensure we only set array data to prevent runtime errors
        if (Array.isArray(isRender.transformedRowsData)) {
            setShownRows(isRender.transformedRowsData);
        } else {
            setShownRows([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricesData]);
    
    useEffect(() => {
        if (!_.isEqual(prevColumnsRef.current, isRender.columns)) {
            const colsObject: any = isRender?.columns?.reduce((acc: any, col: any) => {
                acc[col.id] = col;
                return acc;
            }, {});
            setShownColumns(
                shownColumns?.map((col: any) => {
                    if (Object.prototype.hasOwnProperty.call(colsObject, col.id)) {
                        return {
                            ...col,
                            ...colsObject[col.id],
                        };
                    } else {
                        return col;
                    }
                })
            );
            prevColumnsRef.current = isRender.columns;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRender.columns]);

    const nullValueAriaLabel = i18n.format(rest.nullValueAriaLabel, isRender.transformedData);

    // Render Collapsible Element according to icon position
    const renderCollapsibleElement = (position: string) => (
        <>
            {rest.collapsibleElement === 'Label' ? (
                <span
                    className={`collapsible-label collapsible-label-${position}`}
                    onClick={() => setShowSection(!showSection)}
                >
                    {showSection ? rest.collapsibleHideLabel : rest.collapsibleShowLabel}
                </span>
            ) : (
                <span
                    className={`collapsible-icon collapsible-icon-${position}`}
                    onClick={() => setShowSection(!showSection)}
                >
                    <i className={showSection ? rest.iconHideContent : rest.iconShowContent} />
                </span>
            )}
        </>
    );

    const renderWidgets = (wProps: any, data: any) => {
        switch (wProps?.name) {
            case 'iconIndicator':
                return (
                    <div className={`iconIndicator-${wProps?.type}`}>
                        <IconIndicator
                            _data={{ value: data }}
                            activeIconName={rest.iconIndicators?.[wProps?.type]?.activeIconName}
                            inactiveIconName={rest.iconIndicators?.[wProps?.type]?.inactiveIconName}
                        />
                    </div>
                );
            case 'riskIndicator':
                return (
                    <div className={`riskIndicator-${wProps?.type}`}>
                        <RiskIndicator _data={{ value: { sortOrder: data } }} />
                    </div>
                );
        }
    };

    return (
        <>
            <div
                css={css`
                    ${!theme ? tableStyles : themetableStyles}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles || ''}
                `}
                className={
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }
                data-table-widget-id={props.widgetInstanceId}
            >
                {isRender.title ? (
                    <div className="table-title">
                        {rest.isCollapsible &&
                            rest.collapsibleElementPosition === 'Left' &&
                            renderCollapsibleElement('left')}
                        <HeaderWrapper
                            headings={rest?.chartTitles}
                            data={data}
                            disableTabStaticData={
                                globalDisableTabStaticData || rest.disableTabStaticData
                            }
                        />
                        {rest.isCollapsible &&
                            rest.collapsibleElementPosition === 'Right' &&
                            renderCollapsibleElement('right')}
                    </div>
                ) : (
                    ''
                )}
                {rest.enableColumnToggling && (
                    <baseComponents.multiSelectModal.MultiSelectModal
                        selectableItems={
                            isRender?.columns?.map((c: TableColumn) => ({
                                ...c,
                                label: i18n.format(c.label || '', data),
                            })) || []
                        }
                        modalTitle={i18n.format(
                            rest?.columnTogglingTitle || 'Show / Hide Columns',
                            data
                        )}
                        modalSubtitle={i18n.format(
                            rest?.columnTogglingSubtitle ||
                                'Select the columns you want to display',
                            data
                        )}
                        selectAllText={i18n.format(
                            rest?.columnTogglingSelectAllText || 'Select All',
                            data
                        )}
                        deselectAllText={i18n.format(
                            rest?.columnTogglingDeselectAllText || 'Deselect All',
                            data
                        )}
                        saveText={i18n.format(rest?.columnTogglingSaveText || 'Save', data)}
                        cancelText={i18n.format(rest?.columnTogglingCancelText || 'Cancel', data)}
                        selectedItems={shownColumns}
                        setSelectedItems={setShownColumns}
                    />
                )}
                {rest.enableRowToggling && (
                    <baseComponents.multiSelectModal.MultiSelectModal
                        selectableItems={
                            (shownRows?.length > 0 ? shownRows : isRender?.transformedRowsData)?.map((r: any, i: number) => ({
                                ...r,
                                id: `${i}`,
                                label: trimEnd(i18n.format(rest.rowTogglingLabelDataKey, { ...r })),
                            })) || []
                        }
                        modalTitle={i18n.format(rest?.rowTogglingTitle || 'Show / Hide Rows', data)}
                        modalSubtitle={i18n.format(
                            rest?.rowTogglingSubtitle || 'Select the rows you want to display',
                            data
                        )}
                        selectAllText={i18n.format(
                            rest?.rowTogglingSelectAllText || 'Select All',
                            data
                        )}
                        deselectAllText={i18n.format(
                            rest?.rowTogglingDeselectAllText || 'Deselect All',
                            data
                        )}
                        saveText={i18n.format(rest?.rowTogglingSaveText || 'Save', data)}
                        cancelText={i18n.format(rest?.rowTogglingCancelText || 'Cancel', data)}
                        selectedItems={shownRows?.length > 0 ? shownRows : (isRender?.transformedRowsData || [])}
                        setSelectedItems={setShownRows}
                        numberOfColumns={1}
                    />
                )}
                {isRender.table ? (
                    <>
                        <Placeholder
                            data-testid="top-placeholder"
                            contentPlaceHolderId={`top-${CHILDREN_PLACEHOLDER_ID}`}
                            parentWidgetInstanceId={widgetInstance?.id}
                            data={{
                                ...data,
                                downloadFileColumns: shownColumns
                                    ?.filter((c) => c.label && c.value)
                                    ?.map((c) => ({
                                        label: c.downloadLabel || c.label,
                                        value: c.downloadValue || c.value,
                                    })) as { label: string; value: string }[],
                            }}
                        />
                        <div className="table-container">
                            <table
                                id={rest.id}
                                className={`table ${rest.isCollapsible ? 'collapsible-table' : ''}`}
                            >
                                {shownColumns?.filter((c) => !!c.label).length ? (
                                    <thead>
                                        <tr>
                                            {shownColumns?.map((c: TableColumn, index: number) => {
                                                let headingMarkers: any = '';

                                                try {
                                                    headingMarkers =
                                                        !_.isEmpty(annotator) &&
                                                        annotator?.markersByMarkerTargetId(
                                                            `${c.markerLabel}`
                                                        );
                                                } catch (e) {
                                                    console.log(e);
                                                }

                                                return c.label ? (
                                                    <th
                                                        tabIndex={tabIndex}
                                                        key={index}
                                                        dangerouslySetInnerHTML={{
                                                            __html: i18n.format(c.label || '', {
                                                                ...data,
                                                                _markers: headingMarkers,
                                                            }),
                                                        }}
                                                    />
                                                ) : (
                                                    <th key={index} tabIndex={tabIndex}></th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                ) : null}

                                <tbody>
                                    {/* Use shownRows if available, otherwise fall back to transformedRowsData */}
                                    {(shownRows?.length > 0 ? shownRows : isRender?.transformedRowsData)?.length ? (
                                        (shownRows?.length > 0 ? shownRows : isRender?.transformedRowsData)?.map((r: any, index: number) => {
                                            const isCollapsed =
                                                rest.isCollapsible &&
                                                rest.excludedRow !== index &&
                                                !showSection;

                                            return (
                                                <tr
                                                    key={index}
                                                    className={`${
                                                        isCollapsed ? 'hide-row' : 'show-all'
                                                    } ${
                                                        rest.excludedRow === index && 'exclude-row'
                                                    }`}
                                                >
                                                    {shownColumns?.map(
                                                        (c: TableColumn, id: number) => {
                                                            const uniqueColumn = c.columnIsUnique;
                                                            const localeClassName =
                                                                data.locale === 'fr-CA'
                                                                    ? 'french'
                                                                    : '';
                                                            const uniqueClassName = uniqueColumn
                                                                ? 'column-unique-style'
                                                                : '';

                                                            let templateStr = '';

                                                            if (c.value) {
                                                                templateStr = !c.isSerial
                                                                    ? c.value
                                                                    : `${index + 1}${c.value}`;
                                                            }

                                                            const boundHtml =
                                                                trimEnd(
                                                                    i18n.format(templateStr, {
                                                                        ...r,
                                                                        custom: JsonQuery(
                                                                            `${c.customDataKey}`,
                                                                            {
                                                                                data: r,
                                                                            }
                                                                        )?.value,
                                                                    })
                                                                ) || i18n.t('', '_', 'dash');

                                                            const linkUrl = trimEnd(
                                                                i18n.format(c.linkUrl, {
                                                                    ...r,
                                                                    custom: JsonQuery(
                                                                        `${c.customDataKey}`,
                                                                        {
                                                                            data: r,
                                                                        }
                                                                    )?.value,
                                                                })
                                                            );

                                                            if (r?.renderAsIcon && id !== 0) {
                                                                /* renderAsIcon will be used by the transform 
                                                                    function & its expected to be a boolean. id !==0
                                                                    gives room for column title
                                                                */
                                                                return (
                                                                    <td
                                                                        tabIndex={tabIndex}
                                                                        className={`${localeClassName} ${uniqueClassName}`}
                                                                        key={`${c.value}${index}${id}`}
                                                                    >
                                                                        {boundHtml !== '&mdash;' ? (
                                                                            <IconIndicator
                                                                                _data={{
                                                                                    value: boundHtml,
                                                                                }}
                                                                                activeIconName={
                                                                                    rest
                                                                                        .iconIndicators?.[0]
                                                                                        ?.activeIconName
                                                                                }
                                                                                inactiveIconName={
                                                                                    rest
                                                                                        .iconIndicators?.[0]
                                                                                        .inactiveIconName
                                                                                }
                                                                                altText={`${boundHtml} ${r?.altText}`}
                                                                            />
                                                                        ) : (
                                                                            <p
                                                                                key={`${c.value}${index}${id}`}
                                                                            >
                                                                                <span className="sr-only">
                                                                                    {
                                                                                        nullValueAriaLabel
                                                                                    }
                                                                                </span>
                                                                                {'__'}
                                                                            </p>
                                                                        )}
                                                                    </td>
                                                                );
                                                            } else if (
                                                                !c.isOnlyText &&
                                                                r?.isWidget
                                                            ) {
                                                                return (
                                                                    <td
                                                                        tabIndex={tabIndex}
                                                                        className={`${localeClassName} ${uniqueClassName}`}
                                                                        key={`${c.value}${index}${id}`}
                                                                    >
                                                                        {renderWidgets(
                                                                            r?.wProps,
                                                                            boundHtml
                                                                        )}
                                                                    </td>
                                                                );
                                                            } else if (c.isResourceString) {
                                                                return (
                                                                    <td
                                                                        tabIndex={tabIndex}
                                                                        className={`${localeClassName} ${uniqueClassName}`}
                                                                        key={`${c.value}${index}${id}`}
                                                                    >
                                                                        {
                                                                            data?.resourceStrings[
                                                                                boundHtml
                                                                            ]
                                                                        }
                                                                    </td>
                                                                );
                                                            } else if (c.isLink) {
                                                                return (
                                                                    <td
                                                                        tabIndex={tabIndex}
                                                                        className={`link-cell ${localeClassName} ${uniqueClassName}`}
                                                                        key={`${c.value}${index}${id}`}
                                                                    >
                                                                        <a
                                                                            href={linkUrl}
                                                                            id="link"
                                                                            rel="noreferrer"
                                                                            target="_blank"
                                                                        >
                                                                            <i
                                                                                className={`link-icon ${c.linkIcon}`}
                                                                            />
                                                                            <span
                                                                                className="link-name"
                                                                                dangerouslySetInnerHTML={{
                                                                                    __html: boundHtml,
                                                                                }}
                                                                            />
                                                                        </a>
                                                                    </td>
                                                                );
                                                            } else {
                                                                return boundHtml !== '—' ? (
                                                                    <td
                                                                        tabIndex={tabIndex}
                                                                        className={`${localeClassName} ${uniqueClassName}`}
                                                                        key={`${c.value}${index}${id}`}
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: boundHtml,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <td
                                                                        tabIndex={tabIndex}
                                                                        key={`${c.value}${index}${id}`}
                                                                    >
                                                                        <span className="sr-only">
                                                                            {nullValueAriaLabel}
                                                                        </span>
                                                                        {boundHtml}
                                                                    </td>
                                                                );
                                                            }
                                                        }
                                                    )}
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr
                                            className="empty-rows-message"
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(rest.emptyRowsMessage, data),
                                            }}
                                        />
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Placeholder
                            data-testid="bottom-placeholder"
                            contentPlaceHolderId={`bottom-${CHILDREN_PLACEHOLDER_ID}`}
                            parentWidgetInstanceId={widgetInstance?.id}
                            data={{
                                ...data,
                                downloadFileColumns: shownColumns
                                    ?.filter((c) => c.label && c.value)
                                    ?.map((c) => ({
                                        label: c.downloadLabel || c.label,
                                        value: c.downloadValue || c.value,
                                    })) as { label: string; value: string }[],
                            }}
                        />
                    </>
                ) : isRender.newFundDataMessage ? (
                    <p tabIndex={tabIndex} className="new-fund-data-message">
                        {i18n.format(rest.newFundDataMessage, data)}
                    </p>
                ) : (
                    <p
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(rest.noDataMessage, data),
                        }}
                    />
                )}
            </div>
        </>
    );
};

export default Table;
